/**
 * EmbeddingConfigEditDialog Component
 * Dialog for creating and editing embedding provider configurations
 * Migrated to BaseDialog pattern (Phase 3.2)
 */

import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { BugReport as TestIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { BaseDialog } from '../ui/BaseDialog';
import type { EmbeddingConfig, EmbeddingProviderType } from '../../api/ApiClient';

interface EmbeddingProvider {
  value: string;
  label: string;
  description: string;
  default_model: string;
  default_vector_size: number;
  required_config: string[];
  optional_config: string[];
  is_active: boolean;
}

interface EmbeddingConfigEditDialogProps {
  open: boolean;
  config: Partial<EmbeddingConfig> | null;
  providers: EmbeddingProvider[];
  onClose: () => void;
  onSave: (config: Partial<EmbeddingConfig>) => void;
  onTest: (config: Partial<EmbeddingConfig>) => void;
}

export const EmbeddingConfigEditDialog: React.FC<EmbeddingConfigEditDialogProps> = ({
  open,
  config,
  providers,
  onClose,
  onSave,
  onTest,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Partial<EmbeddingConfig>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (config) {
      setFormData({ ...config });
    }
    // Clear error state when dialog opens/config changes
    setError(null);
    setLoading(false);
  }, [config, open]);

  const selectedProvider = providers.find(p => p.value === formData.provider);

  const handleProviderChange = (provider: string) => {
    const providerInfo = providers.find(p => p.value === provider);
    if (providerInfo) {
      setFormData({
        ...formData,
        provider: provider as EmbeddingProviderType,
        model: providerInfo.default_model,
        vector_size: providerInfo.default_vector_size,
        provider_config: {},
      });
    }
  };

  const handleProviderConfigChange = (key: string, value: string) => {
    setFormData({
      ...formData,
      provider_config: {
        ...formData.provider_config,
        [key]: value,
      },
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      await onSave(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    try {
      setLoading(true);
      setError(null);
      await onTest(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={config?.id ? t('embedding.dialog.editTitle') : t('embedding.dialog.addTitle')}
      maxWidth="md"
      fullWidth
      loading={loading}
      error={error}
      actions={
        <>
          <Button onClick={onClose} disabled={loading}>
            {t('embedding.dialog.cancel')}
          </Button>
          <Button onClick={handleTest} startIcon={<TestIcon />} disabled={loading}>
            {t('embedding.dialog.test')}
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {t('embedding.dialog.save')}
          </Button>
        </>
      }
    >
      <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={12}>
            <TextField
              fullWidth
              label={t('embedding.form.displayName')}
              value={formData.display_name || ''}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label={t('embedding.form.description')}
              multiline
              rows={2}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>{t('embedding.form.provider')}</InputLabel>
              <Select
                value={formData.provider || ''}
                onChange={(e) => handleProviderChange(e.target.value)}
              >
                {providers.map((provider) => (
                  <MenuItem key={provider.value} value={provider.value}>
                    {provider.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('embedding.form.model')}
              value={formData.model || ''}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={t('embedding.form.vectorSize')}
              type="number"
              value={formData.vector_size || ''}
              onChange={(e) => setFormData({ ...formData, vector_size: parseInt(e.target.value) })}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={t('embedding.form.batchSize')}
              type="number"
              value={formData.batch_size || ''}
              onChange={(e) => setFormData({ ...formData, batch_size: parseInt(e.target.value) })}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={t('embedding.form.timeout')}
              type="number"
              value={formData.timeout || ''}
              onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) })}
            />
          </Grid>

          {selectedProvider && (
            <>
              <Grid size={12}>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('embedding.form.providerConfiguration')}
                  </Typography>
                </Divider>
              </Grid>

              {selectedProvider.required_config.map((configKey) => (
                <Grid size={{ xs: 12, md: 6 }} key={configKey}>
                  <TextField
                    fullWidth
                    label={
                      configKey === 'endpoint' ? t('embedding.form.azureOpenAIEndpoint') :
                      configKey === 'api_key' ? t('embedding.form.azureOpenAIKey') :
                      configKey === 'base_url' ? t('embedding.form.baseUrl') :
                      configKey === 'model_name' ? t('embedding.form.modelName') :
                      configKey
                    }
                    type={configKey.includes('key') || configKey.includes('password') ? 'password' : 'text'}
                    value={formData.provider_config?.[configKey] || ''}
                    onChange={(e) => handleProviderConfigChange(configKey, e.target.value)}
                    required
                    placeholder={
                      configKey === 'base_url' && selectedProvider.value === 'ollama' ? 'http://host.docker.internal:11434' :
                      configKey === 'base_url' && selectedProvider.value === 'tei' ? 'http://host.docker.internal:8080' :
                      undefined
                    }
                    helperText={
                      configKey === 'endpoint' ?
                        t('embedding.form.endpointHelp') :
                      configKey === 'api_key' ?
                        t('embedding.form.apiKeyHelp') :
                      configKey === 'base_url' && selectedProvider.value === 'ollama' ?
                        t('embedding.form.ollamaUrlHelp') :
                      configKey === 'base_url' && selectedProvider.value === 'tei' ?
                        t('embedding.form.teiUrlHelp') :
                      undefined
                    }
                  />
                </Grid>
              ))}

              {selectedProvider.optional_config.map((configKey) => (
                <Grid size={{ xs: 12, md: 6 }} key={configKey}>
                  <TextField
                    fullWidth
                    label={
                      configKey === 'api_version' ? t('embedding.form.apiVersion') :
                      configKey === 'region' ? t('embedding.form.region') :
                      configKey === 'organization' ? t('embedding.form.organization') :
                      `${configKey} (${t('embedding.form.optional')})`
                    }
                    value={formData.provider_config?.[configKey] || ''}
                    onChange={(e) => handleProviderConfigChange(configKey, e.target.value)}
                    helperText={
                      configKey === 'api_version' ?
                        t('embedding.form.apiVersionHelp') :
                      configKey === 'region' ?
                        t('embedding.form.regionHelp') :
                        undefined
                    }
                  />
                </Grid>
              ))}
            </>
          )}
        </Grid>
    </BaseDialog>
  );
};
